import { API_BASE_URL } from "../utils/adminHelpers";

function createRequestError(payload, status) {
  const error = new Error(payload.message || "Request failed.");
  error.status = status;
  error.code = payload.code || "";
  error.data = payload;
  return error;
}

export async function requestJson(endpoint, options = {}) {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, options);
  const contentType = response.headers.get("content-type") || "";
  const payload = contentType.includes("application/json")
    ? await response.json()
    : { message: await response.text() };

  if (!response.ok) {
    throw createRequestError(payload, response.status);
  }

  return payload;
}

export function loginAdmin(payload) {
  return requestJson("/instateam/auth/admin/login", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function getCurrentAdmin(token) {
  return requestJson("/instateam/auth/admin/me", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function getAdminSettings(token) {
  return requestJson("/instateam/auth/admin/settings", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function updateAdminSettings(token, payload) {
  return requestJson("/instateam/auth/admin/settings", {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function getAdminDashboard(token, params = {}) {
  const searchParams = new URLSearchParams();
  if (params.date) searchParams.set("date", params.date);
  if (params.dateFrom) searchParams.set("dateFrom", params.dateFrom);
  if (params.dateTo) searchParams.set("dateTo", params.dateTo);
  if (params.attendancePeriod) searchParams.set("attendancePeriod", params.attendancePeriod);
  if (params.leavePeriod) searchParams.set("leavePeriod", params.leavePeriod);
  const suffix = searchParams.toString() ? `?${searchParams.toString()}` : "";

  return requestJson(`/instateam/dashboard/admin${suffix}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function createAdminDashboardTodo(token, payload) {
  return requestJson("/instateam/dashboard/admin/todos", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateAdminDashboardTodo(token, todoId, payload) {
  return requestJson(`/instateam/dashboard/admin/todos/${todoId}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function createAdminDashboardNote(token, payload) {
  return requestJson("/instateam/dashboard/admin/notes", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function deleteAdminDashboardNote(token, noteId) {
  return requestJson(`/instateam/dashboard/admin/notes/${noteId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function changeCurrentAdminPassword(token, payload) {
  return requestJson("/instateam/auth/admin/change-password", {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function listAdminUsers(token) {
  return requestJson("/instateam/admin-users", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function createAdminUser(token, payload) {
  return requestJson("/instateam/admin-users", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateAdminUserAccess(token, adminUserId, payload) {
  return requestJson(`/instateam/admin-users/${adminUserId}/access`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateAdminUserPassword(token, adminUserId, newPassword) {
  return requestJson(`/instateam/admin-users/${adminUserId}/password`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ newPassword }),
  });
}

export function deleteAdminUser(token, adminUserId) {
  return requestJson(`/instateam/admin-users/${adminUserId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function listEmployees(token) {
  return requestJson("/instateam/employees", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function createEmployee(token, formData) {
  return requestJson("/instateam/employees", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });
}

export function getEmployeeById(token, employeeId) {
  return requestJson(`/instateam/employees/${employeeId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function updateEmployee(token, employeeId, formData) {
  return requestJson(`/instateam/employees/${employeeId}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });
}

export function updateEmployeePassword(token, employeeId, newPassword) {
  return requestJson(`/instateam/employees/${employeeId}/password`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ newPassword }),
  });
}

export function updateEmployeeStatus(token, employeeId, status) {
  return requestJson(`/instateam/employees/${employeeId}/status`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ status }),
  });
}

export function listDepartments(token) {
  return requestJson("/instateam/departments", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function createDepartment(token, payload) {
  return requestJson("/instateam/departments", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateDepartment(token, departmentId, payload) {
  return requestJson(`/instateam/departments/${departmentId}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateDepartmentStatus(token, departmentId, status) {
  return requestJson(`/instateam/departments/${departmentId}/status`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ status }),
  });
}

export function listDesignations(token) {
  return requestJson("/instateam/designations", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function createDesignation(token, payload) {
  return requestJson("/instateam/designations", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateDesignation(token, designationId, payload) {
  return requestJson(`/instateam/designations/${designationId}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateDesignationStatus(token, designationId, status) {
  return requestJson(`/instateam/designations/${designationId}/status`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ status }),
  });
}

export function getAnnouncementOverview(token, search = "") {
  const suffix = search ? `?search=${encodeURIComponent(search)}` : "";

  return requestJson(`/instateam/announcements/admin/overview${suffix}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function createAnnouncement(token, payload) {
  return requestJson("/instateam/announcements/admin", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateAnnouncement(token, announcementId, payload) {
  return requestJson(`/instateam/announcements/admin/${announcementId}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateAnnouncementStatus(token, announcementId, status) {
  return requestJson(`/instateam/announcements/admin/${announcementId}/status`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ status }),
  });
}

export function archiveAnnouncement(token, announcementId) {
  return requestJson(`/instateam/announcements/admin/${announcementId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function getDocumentOverview(token, params = {}) {
  const searchParams = new URLSearchParams();
  if (params.search) searchParams.set("search", params.search);
  if (params.department && params.department !== "all") searchParams.set("department", params.department);
  if (params.status && params.status !== "all") searchParams.set("status", params.status);
  const suffix = searchParams.toString() ? `?${searchParams.toString()}` : "";

  return requestJson(`/instateam/documents/admin/overview${suffix}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function getEmployeeDocumentsForAdmin(token, employeeId) {
  return requestJson(`/instateam/documents/admin/employees/${employeeId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function requestEmployeeDocument(token, employeeId, payload) {
  return requestJson(`/instateam/documents/admin/employees/${employeeId}/request`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function reviewEmployeeDocument(token, requestId, payload) {
  return requestJson(`/instateam/documents/admin/requests/${requestId}/review`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function getComplaintOverview(token, params = {}) {
  const searchParams = new URLSearchParams();
  if (params.search) searchParams.set("search", params.search);
  if (params.status && params.status !== "all") searchParams.set("status", params.status);
  if (params.priority && params.priority !== "all") searchParams.set("priority", params.priority);
  if (params.category && params.category !== "all") searchParams.set("category", params.category);
  const suffix = searchParams.toString() ? `?${searchParams.toString()}` : "";

  return requestJson(`/instateam/complaints/admin/overview${suffix}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function updateComplaint(token, complaintId, payload) {
  return requestJson(`/instateam/complaints/admin/${complaintId}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function createAdminComplaint(token, payload) {
  return requestJson("/instateam/complaints/admin", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}
